import type { GmRewardAction } from '@/components/features/gamification/model/types/gm-statuses';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useCallback } from 'react';
import { useGmBarPlayerData } from '@/components/features/gamification/model/hooks/useGmBarPlayerData';
import { useUnclaimedGamificationRewardsCount } from '@/components/features/gamification/modules/rewards/hooks/useUnclaimedGamificationRewardsCount';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { useProgressRouterIntel } from '@/hooks/useProgressRouter';
import openModal from '@/modals/utils/openModal';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { useManageStatusUpActions } from './useManageStatusUpActions';

const GAMIFICATION_REWARD_ACTION_TARGETS = {
  'level-up-card': {
    mobilePath: '/gamification/level-up',
    modalName: 'gm-card-level-up',
  },
  'status-up-card': {
    mobilePath: '/gamification/status-up',
    modalName: 'gm-card-statuses',
  },
} as const satisfies Record<Exclude<GmRewardAction, 'open-support'>, {
  mobilePath: string;
  modalName: Parameters<typeof openModal>[0];
}>;

export const useOpenGamificationRewardAction = () => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const router = useProgressRouterIntel();
  const { data: user } = useUserMeQuery();
  const { playerProfile } = useGmBarPlayerData();
  // Grouped rewards are the lightweight source of truth for badges and for deciding
  // whether the reward should enter the claim flow or just show the cards.
  const { levelUpRewards, statusUpRewards } = useUnclaimedGamificationRewardsCount();
  const { handleScratchAction: handleLevelUpScratchAction } = useManageStatusUpActions('level-up');

  return useCallback((action?: GmRewardAction) => {
    if (!action) {
      return;
    }

    if (action === 'open-support') {
      showIntercom();
      return;
    }

    if (!user?.id) {
      return;
    }

    const target = GAMIFICATION_REWARD_ACTION_TARGETS[action];

    // Status-up has two flows:
    // 1. Unclaimed reward exists: show congratulations first, then its CTA continues to scratch-card claim.
    // 2. No unclaimed reward: keep the original statuses slider/card view as an informational fallback.
    if (action === 'status-up-card' && statusUpRewards > 0 && playerProfile?.progressStatus?.id) {
      openModal('gm-congratulations-status-level', { statusId: playerProfile.progressStatus.id });
      return;
    }

    if (action === 'level-up-card' && levelUpRewards > 0) {
      handleLevelUpScratchAction();
      return;
    }

    if (isMobile) {
      router.push(`${target.mobilePath}?userId=${user.id}`);
      return;
    }

    openModal(target.modalName);
  }, [handleLevelUpScratchAction, isMobile, levelUpRewards, playerProfile?.progressStatus?.id, router, statusUpRewards, user?.id]);
};
